// Activate Account
// ------------------
// Two-step account activation flow for new users.
// Step 1: Set display name + password (ActivatePageOne)
// Step 2: Upload avatar (ActivatePageTwo)
// Dark-themed, matches the auth page visual language.

import React, { useState, useCallback, useEffect } from "react";

import ActivatePageOne from "@/components/activateAccount/ActivatePageOne";
import ActivatePageTwo from "@/components/activateAccount/ActivatePageTwo";
import { MFA_CHALLENGE_PAGE, NIMIS_WEBSITE_URL } from "@/constants/definitions";
import CognitoLoginClass from "@/models/cognitoLogin";
import { loadDashboard } from "@/utils/navigation";
import { persistPreferredUsername } from "@/services/dataListOperations.service";

interface ActivateAccountProps {
  cognitologin: CognitoLoginClass;
  username: string;
  oldpassword?: string;
  getUserDetails: () => Promise<void>;
  // Router navigate — accepts optional route state so the MFA mode can be passed
  // to the /mfa page.
  navigate: (path: string, options?: { state?: unknown; replace?: boolean }) => void;
}

export default function ActivateAccount({
  cognitologin,
  username,
  oldpassword,
  getUserDetails,
  navigate,
}: ActivateAccountProps) {
  const [pageOneActive, setPageOneActive] = useState(true);
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState<string | null>(null);

  useEffect(() => {
    const rootElement = document.getElementById("root");
    if (rootElement) rootElement.className = "root";
  }, []);

  const handlePageOneComplete = useCallback((newPassword: string) => {
    setPassword(newPassword);
    setPageOneActive(false);
  }, []);

  // Under MfaConfiguration=REQUIRED, activation stops at the TOTP enrolment step:
  // the password is set but no session exists until MFA is configured. Route to
  // the /mfa setup page instead of the avatar step (avatar is settable later
  // from the profile). accountLabel names the authenticator-app entry;
  // pendingDisplayName is persisted by the MFA page once a session exists.
  const handleMfaSetupRequired = useCallback((chosenDisplayName: string) => {
    navigate(MFA_CHALLENGE_PAGE, {
      state: { mode: "setup", accountLabel: username, pendingDisplayName: chosenDisplayName },
    });
  }, [navigate, username]);

  const handlePageTwoComplete = useCallback(async () => {
    try {
      await cognitologin.login(username, password || "");
      // Persist the preferred username chosen on step one now that a session
      // exists. Non-fatal: a taken name (or transient error) shouldn't block
      // entry — the user can set it later from their profile.
      await persistPreferredUsername(displayName).catch(() => {});
      await getUserDetails();
      loadDashboard(navigate);
    } catch (activationError) {
      console.error("Failed to complete account activation:", activationError);
    }
  }, [cognitologin, username, password, displayName, getUserDetails, navigate]);

  return (
    <div data-theme="dark" className="relative" style={{ background: "hsla(222, 70%, 8%, 0.85)", minHeight: "calc(100vh + 1px)" }}>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 60% 50% at 20% 30%, rgba(59, 130, 246, 0.08) 0%, transparent 70%),
            radial-gradient(ellipse 50% 40% at 80% 70%, rgba(14, 165, 233, 0.05) 0%, transparent 70%)
          `,
        }}
      />

      <div className="relative z-10 mx-auto flex w-full max-w-6xl items-center px-4 sm:px-6 pt-6 pb-6">
        <a href={NIMIS_WEBSITE_URL}>
          <img src="/nimis-intelligence-white-logo.png" alt="NIMIS Intelligence" className="h-12 w-auto" />
        </a>
      </div>

      <div className="relative z-10 flex justify-center pt-[12vh]">
        <div className="w-full max-w-sm mx-4">
          <div className="rounded-xl bg-white/[0.03] backdrop-blur-sm border border-white/[0.08] p-8">
            <div className="text-center mb-6">
              <h1 className="text-xl font-semibold text-white">Activate your account</h1>

              {/* Step indicator */}
              <div className="flex items-center justify-center gap-3 mt-4">
                <div className={`flex items-center gap-1.5 text-xs font-medium ${pageOneActive ? "text-blue-400" : "text-white/30"}`}>
                  <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold ${pageOneActive ? "bg-blue-600 text-white" : "bg-white/10 text-white/40"}`}>1</span>
                  Credentials
                </div>
                <div className="w-6 h-px bg-white/10" />
                <div className={`flex items-center gap-1.5 text-xs font-medium ${!pageOneActive ? "text-blue-400" : "text-white/30"}`}>
                  <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold ${!pageOneActive ? "bg-blue-600 text-white" : "bg-white/10 text-white/40"}`}>2</span>
                  Avatar
                </div>
              </div>
            </div>

            {pageOneActive ? (
              <ActivatePageOne
                cognitologin={cognitologin}
                username={username}
                oldpassword={oldpassword}
                pageOneStateChangeHandler={handlePageOneComplete}
                updateDisplayName={setDisplayName}
                onMfaSetupRequired={handleMfaSetupRequired}
              />
            ) : (
              <ActivatePageTwo
                cognitologin={cognitologin}
                pageTwoStateChangeHandler={handlePageTwoComplete}
                displayName={displayName}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
